import { Stack, useTheme } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';
import { v4 as uuidv4 } from 'uuid';

import { useLocalStorage, useMeta } from '@woovi/hooks';
import type { FetchKeyProp } from '@woovi/relay';
import { useMutationCallbacks, withRelayBoundary } from '@woovi/relay';
import { CHARGE_STATUS_ENUM, EventType } from '@woovi/shared';
import { ClickPixButton, ClickPixLogo, ErrorView } from '@woovi/ui';
import type { CLICK_PIX_BUTTON_VARIANT } from '@woovi/ui';
import { centsToFloat, CUSTOMER_REGISTER_FIELD_ENUM } from '@woovi/utils';

import type { WooviPayButtonApp_charge$key } from './__generated__/WooviPayButtonApp_charge.graphql.ts';
import type { WooviPayButtonAppQuery } from './__generated__/WooviPayButtonAppQuery.graphql.ts';
import { WooviPayButton } from './WooviPayButton.tsx';
import WooviPayPlugin from './WooviPayPlugin.tsx';
import type { AdditionalInfoItem } from '../dataLayer/OpenPixStore.tsx';
import { getClickpixHostname } from '../getClickpixHostname.ts';
import type { ChargeAddMutation } from '../plugin/__generated__/ChargeAddMutation.graphql.ts';
import { ChargeAdd } from '../plugin/ChargeAddMutation.tsx';
import MetaPixelScript from '../plugin/MetaPixelScript.tsx';
import { notifyListeners } from '../plugin/notifyListeners.tsx';
import PluginSwipeableDialog from '../plugin/PluginSwipeableDialog.tsx';
import PluginSwipeableDialogContentLoading from '../plugin/PluginSwipeableDialogContentLoading.tsx';

export type PaymentData = {
  requestId: string;
  methodName: string;
  details: Details;
  shippingAddress: ShippingAddress;
  shippingOption: string;
  payerName: string;
  payerEmail: string;
  payerPhone: string;
};
export type Details = {
  bobbucks_token_id: string;
  message: string;
};
export type ShippingAddress = {
  country: string;
  addressLine: string[];
  region: string;
  city: string;
  dependentLocality: string;
  postalCode: string;
  sortingCode: string;
  organization: string;
  recipient: string;
  phone: string;
};

type WooviPayCustomerData = {
  name?: string;
  email?: string;
};

type WooviPayButtonProps = {
  value: number;
  customerRegisterFields?: CUSTOMER_REGISTER_FIELD_ENUM[];
  additionalInfo?: AdditionalInfoItem[];
  redirectUrl?: string;
  variant?: CLICK_PIX_BUTTON_VARIANT;
  correlationID?: string;
};

const WooviPayButtonApp = (props: WooviPayButtonProps): ReactNode => {
  const { customerRegisterFields = [] } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const [correlationID, setCorrelationID] = useState<string | null>(
    props.correlationID || null,
  );

  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(1);
  const [hasChargeCreated, setHasChargeCreated] = useState(false);
  const [localCustomerData, setLocalCustomerData] = useLocalStorage(
    '@woovi/customer-data',
    {},
  );

  const value = props.value || 100;

  const variables = useMemo(
    () => ({
      correlationID,
    }),
    [],
  );

  const response = useLazyLoadQuery<WooviPayButtonAppQuery>(
    graphql`
      query WooviPayButtonAppQuery($correlationID: String) {
        charge(correlationID: $correlationID) {
          ...WooviPayButtonApp_charge
        }
      }
    `,
    variables,
    {
      fetchPolicy: 'store-and-network',
      fetchKey,
    },
  );

  const charge = useFragment<WooviPayButtonApp_charge$key>(
    graphql`
      fragment WooviPayButtonApp_charge on Charge {
        id
        ...Payment_charge
        ...useMeta_charge
        status
        metaPixel {
          token
        }
      }
    `,
    response.charge,
  );

  const [chargeAddMutation, isPending] =
    useMutationCallbacks<ChargeAddMutation>({
      name: 'ChargeAdd',
      mutation: ChargeAdd,
      shouldShowSnackbar: false,
      afterCompleted: (response) => {
        if (response?.ChargeAdd?.error) {
          setError(response.ChargeAdd.error);
          notifyListeners({
            type: EventType.ON_ERROR,
            data: {
              error: response.ChargeAdd.error,
            },
          });
        }

        setFetchKey(fetchKey + 1);
        setHasChargeCreated(true);
      },
      afterError: (error) => {
        notifyListeners({
          type: EventType.ON_ERROR,
          data: {
            error,
          },
        });

        // graphql errors
        if (error?.errors) {
          // improve this error with proper frontend input value validations
          setError(t('Error generating charge, check your input'));

          return;
        }

        if (error) {
          setError(error?.toString());
        }
      },
    });

  const createCharge = (customerData?: WooviPayCustomerData): void => {
    const getCorrelationID = (): string => {
      if (correlationID) {
        return correlationID;
      }

      const newCorrelationID = uuidv4();

      setCorrelationID(newCorrelationID);

      return newCorrelationID;
    };

    if (charge && charge.status === CHARGE_STATUS_ENUM.ACTIVE) {
      setIsOpen(true);

      return;
    }

    setIsOpen(true);

    const getCustomerData = (): {
      customer?: { email: string; name: string };
    } => {
      if (!customerData) {
        return {};
      }

      setLocalCustomerData({
        email: customerData.email,
        name: customerData.name,
        ...localCustomerData,
      });

      return {
        customer: {
          email: customerData.email,
          name: customerData.name,
        },
      };
    };

    const getCustomerRegister = (): {
      customerRegister?: boolean;
      customerRegisterFields?: string[];
    } => {
      if (customerData) {
        return {};
      }

      if (!customerRegisterFields.length) {
        return {};
      }

      const customerRegisterSet = new Set([
        CUSTOMER_REGISTER_FIELD_ENUM.TAX_ID,
        CUSTOMER_REGISTER_FIELD_ENUM.NAME,
        ...customerRegisterFields,
      ]);

      return {
        customerRegister: true,
        customerRegisterFields: Array.from(customerRegisterSet),
      };
    };

    const getRedirectUrl = (): { redirectUrl?: string } => {
      if (!props.redirectUrl) {
        return {};
      }

      return { redirectUrl: props.redirectUrl };
    };

    const getAdditionalInfo = (): { additionalInfo?: AdditionalInfoItem[] } => {
      if (!props.additionalInfo) {
        return {};
      }

      return { additionalInfo: props.additionalInfo };
    };

    const input = {
      value,
      correlationID: getCorrelationID(),
      ...getCustomerData(),
      ...getCustomerRegister(),
      ...getRedirectUrl(),
      ...getAdditionalInfo(),
    };

    const config = {
      variables: {
        input,
      },
    };

    chargeAddMutation(config);
  };

  const hostName = `${getClickpixHostname()}/clickpix`;

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const skipPaymentRequest =
    isSafari || !props.customerRegisterFields?.length || !window.PaymentRequest;

  const handlePaymentRequest = async (): Promise<void> => {
    if (hasChargeCreated || charge?.id) {
      setIsOpen(true);

      return;
    }

    if (skipPaymentRequest) {
      createCharge();

      return;
    }

    const paymentMethods = [
      {
        supportedMethods: hostName,
        data: {},
      },
    ];

    const details = {
      displayItems: [],
      total: {
        label: 'Total',
        amount: {
          value: centsToFloat(value),
          currency: 'BRL',
        },
      },
    };

    const requestPayerEmail = customerRegisterFields.includes(
      CUSTOMER_REGISTER_FIELD_ENUM.EMAIL,
    );

    const requestPayerPhone = customerRegisterFields.includes(
      CUSTOMER_REGISTER_FIELD_ENUM.PHONE,
    );

    // @todo: think of a good shipping model to capture the customer's address
    // for future implementation as physical product delivery with clickpix
    // const requestShipping = customerRegisterFields.includes(
    //   CUSTOMER_REGISTER_FIELD_ENUM.ADDRESS,
    // );

    const options = {
      requestPayerName: true,
      requestPayerEmail,
      requestPayerPhone,
    };

    try {
      const paymentRequest = new PaymentRequest(
        paymentMethods,
        details,
        options,
      );

      const can = await paymentRequest.canMakePayment();

      if (!can) {
        createCharge();

        return;
      }

      const paymentResponse = await paymentRequest.show();

      paymentResponse.complete('success');

      const paymentDataTest = paymentResponse.toJSON();

      createCharge({
        name: paymentDataTest?.payerName,
        email: paymentDataTest?.payerEmail,
      });
    } catch (error) {
      console.error('Payment request failed', error);
    }
  };

  const getDialogTitle = (): ReactNode => {
    return <ClickPixLogo color={theme.palette.text.primary} size={20} full />;
  };

  const handleOpenChange = (): void => {
    setIsOpen(!isOpen);
  };

  useMeta({ charge });

  const getMetaPixelScript = (): ReactNode => {
    const token = charge?.metaPixel?.token;

    if (!token) {
      return null;
    }

    return <MetaPixelScript token={token} charge={charge} />;
  };

  const getContent = (): ReactNode => {
    if (isPending) {
      return <PluginSwipeableDialogContentLoading />;
    }

    if (error) {
      return <ErrorView error={error} />;
    }

    return (
      <WooviPayPlugin
        correlationID={correlationID}
        error={error}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        parentFetchKey={fetchKey}
        withCard={false}
      />
    );
  };

  return (
    <>
      <Stack>
        <WooviPayButton
          variant={props.variant}
          onClick={handlePaymentRequest}
        />
      </Stack>
      <PluginSwipeableDialog
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        title={getDialogTitle()}
      >
        {getContent()}
        {getMetaPixelScript()}
      </PluginSwipeableDialog>
    </>
  );
};

// button shimmer
export default withRelayBoundary<WooviPayButtonProps & FetchKeyProp>(
  WooviPayButtonApp,
  {
    loadingView: (props) => (
      <ClickPixButton data-testid='woovi-pay-loading' variant={props.variant} />
    ),
  },
);
